import type { KpiItem, PricePackBubbleRow } from 'hooks/ppa/useFetchPricePackArchitecture';

import { normalizePackArchitectureKpiId } from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';

export type KpiOption = {
    id: string;
    label: string;
};

export type TableColumn = {
    key: string;
    label: string;
};

export type CellKpiEntry = {
    formatted: string;
    growthPct: number | null;
};

export type CellData = Record<string, CellKpiEntry>;

export type TableRow = {
    key: string;
    label: string;
    cells: Record<string, CellData>;
};

export const MAX_VISIBLE_COLUMNS = 100;
export const MAX_KPI_SELECTION = 3;
export const TABLE_MIN_METRIC_SELECTION_MESSAGE = 'At least 1 metric must be selected.';
export const TABLE_MAX_METRIC_SELECTION_MESSAGE = 'Maximum 3 metrics can be selected.';
/** Match chart horizontal pagination — only fetch when scrolled to the right edge. */
export const HORIZONTAL_PAGINATION_RIGHT_EDGE_THRESHOLD_PX = 16;
/** Pull back from the right edge after append so the next page does not immediately re-trigger. */
export const TABLE_PAGINATION_SCROLL_RETREAT_PX = 180;
/** Left sticky "segment" column width — must match table column + styles. */
export const TABLE_SEGMENT_COLUMN_WIDTH_PX = 125;
/** Vendor data column width — must match packArchitectureTable.columns + styles. */
export const TABLE_VENDOR_COLUMN_WIDTH_PX = 144;

/**
 * Bubble-size spec (single source of truth, shared between BubbleScatterChart
 * and ReusableBubbleChart so chart canvas width, radius scale, and Y-axis
 * padding are always derived from the exact same numbers):
 *
 *   minRadiusPx = chartHeight x PPA_BUBBLE_RADIUS_MIN_RATIO (1% of height)
 *   maxRadiusPx = chartHeight x PPA_BUBBLE_RADIUS_MAX_RATIO (10% of height)
 *   maxDiameterPx = maxRadiusPx x 2
 *
 * Column pitch deliberately deviates from the original "columnWidth =
 * maxDiameter x 1.2" spec: it's pinned to `TABLE_VENDOR_COLUMN_WIDTH_PX` so
 * every chart column lines up 1:1 with its table column (same width, same
 * scrollable total width), which is what the chart<->table scroll sync
 * relies on for exact alignment. Since the pitch is now fixed instead of
 * growing with the bubble, `maxRadiusPx` is capped from above so a bubble
 * can never outgrow its own column and bleed into a neighbour's.
 */
// Lowered from 0.025 (2.5%) to widen the min-max radius range - small/similar
// values sit closer to minRadius, so a lower floor here gives them more
// pixels of headroom to visually separate from one another.
export const PPA_BUBBLE_RADIUS_MIN_RATIO = 0.01;
export const PPA_BUBBLE_RADIUS_MAX_RATIO = 0.1;
/** Chart column pitch — pinned to the table's fixed column width so columns align; no inter-column gap (table columns are contiguous). */
export const PPA_CHART_COLUMN_WIDTH_PX = TABLE_VENDOR_COLUMN_WIDTH_PX;
export const PPA_CHART_COLUMN_GAP_PX = 0;
/** Ceiling on bubble diameter as a fraction of the (now-fixed) column pitch, so it never bleeds into a neighboring column. */
export const PPA_BUBBLE_DIAMETER_MAX_COLUMN_RATIO = 0.6;
/** Above/below the Y-axis min/max value, as a fraction of maxDiameterPx. */
export const PPA_Y_AXIS_PADDING_TO_MAX_DIAMETER_RATIO = 0.6;
/** Floor so a very short/collapsed container never yields invisible or negative-radius bubbles. */
export const PPA_BUBBLE_MIN_RADIUS_FLOOR_PX = 2;
/**
 * G2's `appendPadding` top inset for the chart (BubbleScatterChart) - shared
 * here so the Y-axis padding math below can account for the same known chrome.
 */
export const PPA_CHART_TOP_APPEND_PADDING_PX = 25;
/**
 * Estimated chrome G2 auto-reserves at the bottom for the *visible* X-axis
 * (line + tick labels + title; the Y-axis itself is fully hidden in this
 * chart). The Y-axis domain padding below is computed as a fraction of
 * `chartHeight`, but that height is the whole container - not the actual
 * plot area, which is smaller once G2 carves out room for this chrome. Left
 * unaccounted, the computed padding maps to fewer on-screen pixels than
 * intended, so the bottommost/largest bubbles visually creep past the
 * x-axis line into the label row.
 */
export const PPA_CHART_X_AXIS_CHROME_HEIGHT_PX = 56;

export type PpaBubbleSizing = {
    minRadiusPx: number;
    maxRadiusPx: number;
    maxDiameterPx: number;
    columnWidthPx: number;
    columnGapPx: number;
};

export const computePpaBubbleSizing = (chartHeightPx: number): PpaBubbleSizing => {
    const safeHeight = Number.isFinite(chartHeightPx) && chartHeightPx > 0 ? chartHeightPx : 0;
    const minRadiusPx = Math.max(PPA_BUBBLE_MIN_RADIUS_FLOOR_PX, safeHeight * PPA_BUBBLE_RADIUS_MIN_RATIO);
    const heightBasedMaxRadiusPx = Math.max(minRadiusPx + 1, safeHeight * PPA_BUBBLE_RADIUS_MAX_RATIO);
    const columnWidthPx = PPA_CHART_COLUMN_WIDTH_PX;
    const columnGapPx = PPA_CHART_COLUMN_GAP_PX;
    const columnPitchPx = columnWidthPx + columnGapPx;
    const columnBasedMaxRadiusPx = (columnPitchPx * PPA_BUBBLE_DIAMETER_MAX_COLUMN_RATIO) / 2;
    const maxRadiusPx = Math.max(minRadiusPx + 1, Math.min(heightBasedMaxRadiusPx, columnBasedMaxRadiusPx));
    const maxDiameterPx = maxRadiusPx * 2;
    return { minRadiusPx, maxRadiusPx, maxDiameterPx, columnWidthPx, columnGapPx };
};
/** Suppress table pagination while chart-driven scroll sync is in flight. */
export const CHART_TO_TABLE_SCROLL_SYNC_GUARD_MS = 200;
/**
 * Same idea as `CHART_TO_TABLE_SCROLL_SYNC_GUARD_MS`, but for the table→chart
 * direction: suppresses the chart's own scroll listener from reacting to the
 * *echo* scroll event fired when the table sync writes to `chart.scrollLeft`.
 * Needs to be a timer (not "until next animation frame") for the same reason
 * the other direction is - the echo scroll event's rAF can end up queued
 * *after* an in-callback rAF reset, so a single-frame guard intermittently
 * resets before the echo is handled, letting a redundant sync cycle through
 * on every table scroll tick.
 */
export const TABLE_TO_CHART_SCROLL_SYNC_GUARD_MS = 200;
export const CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS = [0, 50, 150, 300];
export const TABLE_SCROLL_Y = 480;
export const TABLE_EMPTY_STATE_MIN_HEIGHT = 252;
export const EMPTY_CELL_VALUE = '--';

/** Figma cell heights by number of visible KPI lines (ticks) in a table cell. */
export const TABLE_CELL_HEIGHT_BY_KPI_COUNT: Record<number, number> = {
    1: 44,
    2: 92,
    3: 138,
};

export const getTableCellHeightForKpiCount = (kpiCount: number): number =>
    TABLE_CELL_HEIGHT_BY_KPI_COUNT[Math.min(Math.max(kpiCount, 1), MAX_KPI_SELECTION)] ?? 44;

const CHART_ONLY_SEGMENT_KPI_IDS = new Set(['color_code', 'radius']);

const compactFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
});

export const formatTableKpiCheckboxLabel = (label: string) => label.replace(/\s+per\s+/gi, ' / ');

const isPriceKpi = (kpiId: string, yAxisKpiId: string) =>
    kpiId === yAxisKpiId || /price/i.test(kpiId);

/** K/M/B scale suffixes returned separately by the API — not unit labels like oz or %. */
const isCompactScaleSuffix = (suffix: string) => /^[kmb]+$/i.test(suffix.trim());

const withKpiAffixes = (formattedValue: string, prefix?: string, suffix?: string): string => {
    if (!formattedValue || formattedValue === EMPTY_CELL_VALUE) return formattedValue;

    let next = formattedValue;
    const normalizedPrefix = prefix?.trim() ?? '';
    const normalizedSuffix = suffix?.trim() ?? '';

    if (normalizedPrefix && !next.startsWith(normalizedPrefix)) {
        next = `${normalizedPrefix}${next}`;
    }
    if (normalizedSuffix && !next.endsWith(normalizedSuffix)) {
        next = `${next}${normalizedSuffix}`;
    }

    return next;
};

/** Shared KPI display formatter — prefers API-scaled value + affixes (matches backend MetricFormatter). */
export const formatPackArchitectureKpiValue = (kpi: KpiItem, kpiId: string, yAxisKpiId: string): string => {
    const prefix = kpi.valuePrefix ?? '';
    const suffix = kpi.valueSuffix ?? '';
    const apiValue = (kpi.value ?? '').trim();

    if (apiValue && apiValue !== 'null') {
        return withKpiAffixes(apiValue, prefix, suffix);
    }

    if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
        if (isPriceKpi(kpiId, yAxisKpiId)) {
            const formatted = kpi.raw_value.toLocaleString('en-US', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            });
            return withKpiAffixes(formatted, prefix, suffix);
        }

        const compactSuffix = isCompactScaleSuffix(suffix) ? '' : suffix;
        return withKpiAffixes(compactFormatter.format(kpi.raw_value), prefix, compactSuffix);
    }

    return EMPTY_CELL_VALUE;
};

const buildCellKpiEntry = (kpi: KpiItem, kpiId: string, yAxisKpiId: string): CellKpiEntry => ({
    formatted: formatPackArchitectureKpiValue(kpi, kpiId, yAxisKpiId),
    growthPct: kpi.growth_percentage ?? null,
});

export const getPackArchitectureEntityKey = (entity: { ex_id?: string; description?: string }) =>
    entity.ex_id || entity.description || '';

const getSegmentRowKey = getPackArchitectureEntityKey;

const registerBubbleRepRow = (
    bubbleRepMap: Map<string, { key: string; label: string }>,
    segmentItem: { ex_id?: string; description?: string },
    rowKey: string,
) => {
    if (bubbleRepMap.has(rowKey)) return;

    bubbleRepMap.set(rowKey, {
        key: rowKey,
        label: segmentItem.description || segmentItem.ex_id || rowKey,
    });
};

const mergeSegmentKpis = (
    cell: CellData,
    kpis: KpiItem[] | undefined,
    yAxisKpiId: string,
): CellData => {
    const nextCell = { ...cell };

    for (const kpi of kpis ?? []) {
        if (CHART_ONLY_SEGMENT_KPI_IDS.has(kpi.kpi_id)) continue;
        if (kpi.kpi_id in nextCell) continue;
        nextCell[kpi.kpi_id] = buildCellKpiEntry(kpi, kpi.kpi_id, yAxisKpiId);
    }

    return nextCell;
};

const buildColumnGrid = (
    group: PricePackBubbleRow,
    yAxisKpiId: string,
    bubbleRepMap: Map<string, { key: string; label: string }>,
) => {
    const colKey = group.ex_id || group.description;
    const colCells = new Map<string, CellData>();

    for (const axisGroup of group.data ?? []) {
        for (const segmentItem of axisGroup.data ?? []) {
            const rowKey = getSegmentRowKey(segmentItem);
            if (!rowKey) continue;

            registerBubbleRepRow(bubbleRepMap, segmentItem, rowKey);
            colCells.set(rowKey, mergeSegmentKpis(colCells.get(rowKey) ?? {}, segmentItem.kpis, yAxisKpiId));
        }
    }

    return {
        column: {
            key: colKey,
            label: group.description || group.ex_id || colKey,
        } satisfies TableColumn,
        colCells,
    };
};

export const buildTableData = (rows: PricePackBubbleRow[], yAxisKpiId: string) => {
    const columns: TableColumn[] = [];
    const bubbleRepMap = new Map<string, { key: string; label: string }>();
    const cellGrid = new Map<string, Map<string, CellData>>();

    for (const group of rows) {
        const { column, colCells } = buildColumnGrid(group, yAxisKpiId, bubbleRepMap);
        columns.push(column);
        cellGrid.set(column.key, colCells);
    }

    const tableRows: TableRow[] = Array.from(bubbleRepMap.values()).map(({ key, label }) => {
        const cells: Record<string, CellData> = {};
        for (const [colKey, colCells] of cellGrid) {
            cells[colKey] = colCells.get(key) ?? {};
        }
        return { key, label, cells };
    });

    return { columns, tableRows };
};

const normalizeTableKpiId = normalizePackArchitectureKpiId;

export const findCellKpi = (
    cell: CellData,
    kpiId: string,
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean = (cellKpiId, requestedKpiId) =>
        normalizeTableKpiId(cellKpiId) === normalizeTableKpiId(requestedKpiId),
): CellKpiEntry | undefined => {
    if (cell[kpiId]) return cell[kpiId];
    const match = Object.entries(cell).find(([key]) => matchesKpiId(key, kpiId));
    return match?.[1];
};

export const hasCellDataForKpis = (
    cell: CellData | undefined,
    kpiIds: string[],
    matchesKpiId?: (cellKpiId: string, requestedKpiId: string) => boolean,
) =>
    kpiIds.some((kpiId) => {
        const entry = findCellKpi(cell ?? {}, kpiId, matchesKpiId);
        return entry && entry.formatted !== EMPTY_CELL_VALUE;
    });

export const getPackArchitectureTableFooterLabel = (
    showTableLoader: boolean,
    isAppendingPages: boolean,
    loadedColumnCount: number,
    effectiveRowCount: number,
) => {
    if (showTableLoader) return 'Loading Results';
    if (isAppendingPages) return 'Loading more…';
    return `Showing ${loadedColumnCount} of ${effectiveRowCount || loadedColumnCount} Columns`;
};

export const getPackArchitectureTableEmptyDescription = (
    chartError: string | null,
    isFiltered: boolean,
) => {
    if (chartError) return chartError;
    if (isFiltered) return 'Try adjusting your search';
    return 'No data found for the selected filters';
};

const clampHorizontalScrollLeft = (element: HTMLElement, scrollLeft: number) => {
    const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
    return Math.min(Math.max(0, scrollLeft), maxScrollLeft);
};

/** Syncs Ant Design table header + body horizontal scroll (body-only updates leave a header gap). */
export const setPackArchitectureTableScrollLeft = (
    tableWrapper: HTMLElement | null,
    scrollLeft: number,
) => {
    if (!tableWrapper) return;

    const tableBody = tableWrapper.querySelector('.ant-table-body') as HTMLElement | null;
    const tableHeader =
        (tableWrapper.querySelector('.ant-table-header') as HTMLElement | null) ??
        (tableWrapper.querySelector('.pack-arch-vtable-header-scroll') as HTMLElement | null);
    const headerContent =
        (tableHeader?.querySelector('.ant-table-content') as HTMLElement | null) ??
        (tableWrapper.querySelector('.pack-arch-vtable-header-scroll') as HTMLElement | null);
    const bodyScrollLeft = tableBody ? clampHorizontalScrollLeft(tableBody, scrollLeft) : scrollLeft;

    if (tableBody) {
        tableBody.scrollTo({ left: bodyScrollLeft, behavior: 'auto' });
    }

    if (headerContent) {
        headerContent.scrollTo({ left: bodyScrollLeft, behavior: 'auto' });
    }

    if (tableHeader) {
        tableHeader.scrollTo({ left: bodyScrollLeft, behavior: 'auto' });
    }
};

export const getMaxHorizontalScrollLeft = (element: HTMLElement) =>
    Math.max(0, element.scrollWidth - element.clientWidth);

export const getHorizontalScrollRatio = (element: HTMLElement) => {
    const maxScrollLeft = getMaxHorizontalScrollLeft(element);
    if (maxScrollLeft <= 0) return 0;
    return element.scrollLeft / maxScrollLeft;
};

/** Sync chart→table using scroll ratio so variable text widths stay aligned. */
export const syncTableWrapperScrollFromChart = (
    chartElement: HTMLElement,
    tableWrapper: HTMLElement,
) => {
    const chartRatio = getHorizontalScrollRatio(chartElement);
    const tableBody = tableWrapper.querySelector('.ant-table-body') as HTMLElement | null;
    const tableMaxScrollLeft = tableBody ? getMaxHorizontalScrollLeft(tableBody) : 0;
    setPackArchitectureTableScrollLeft(tableWrapper, chartRatio * tableMaxScrollLeft);
};
